import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./ChangePassword.css";
import { MagasinContext } from "../../../Context/MagasinContext";
import { NewSideBarMagasins } from "../../NewSidebarMagasin";
import axios from "axios";

export default function ChangePasswordMagasin() {
  const magasinContext = useContext(MagasinContext);
  const MagasinId = localStorage.getItem("MagasinId");
  const id = MagasinId || magasinContext.id?.id;
  const navigate = useNavigate();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [popup, setPopup] = useState<{ visible: boolean; title: string; message: string; type: "success" | "error" }>({
    visible: false, title: "", message: "", type: "success"
  });

  /* Password strength helper */
  const getStrength = (pw: string) => {
    if (!pw) return { level: 0, label: "" };
    let score = 0;
    if (pw.length >= 6) score++;
    if (pw.length >= 10) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 2) return { level: 1, label: "Faible" };
    if (score <= 3) return { level: 2, label: "Moyen" };
    return { level: 3, label: "Fort" };
  };

  const strength = getStrength(newPassword);

  const strengthClass = (idx: number) => {
    if (strength.level === 0) return "";
    if (strength.level === 1 && idx < 1) return "weak";
    if (strength.level === 2 && idx < 2) return "medium";
    if (strength.level === 3 && idx < 3) return "strong";
    return "";
  };

  const handleGoBack = () => navigate(-1);

  const handleSubmit = async () => {
    if (!oldPassword) {
      setPopup({ visible: true, title: "Erreur", message: "Veuillez entrer votre mot de passe actuel.", type: "error" });
      setTimeout(() => setPopup(prev => ({...prev, visible: false})), 3000);
      return;
    }
    if (!newPassword || newPassword !== confirmPassword) {
      setPopup({ visible: true, title: "Erreur", message: "Les nouveaux mots de passe ne correspondent pas ou sont vides.", type: "error" });
      setTimeout(() => setPopup(prev => ({...prev, visible: false})), 3000);
      return;
    }

    setLoadingSubmit(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/magasins/${id}/request-change-password`, {
        old_password: oldPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      setPopup({ visible: true, title: "Succès", message: response.data.success || "Votre demande a été envoyée. Veuillez patienter 24 heures et nous vous recontacterons.", type: "success" });
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || "Une erreur est survenue. Veuillez réessayer.";
      setPopup({ visible: true, title: "Erreur", message: errorMsg, type: "error" });
    } finally {
      setLoadingSubmit(false);
      setTimeout(() => setPopup(prev => ({...prev, visible: false})), 5000);
    }
  };

  const popupStyle = (type: "success" | "error") => ({
    position: "fixed" as const,
    top: "20px",
    right: "20px",
    zIndex: 9999,
    padding: "20px 24px",
    borderRadius: "16px",
    border: "1px solid #ffffff",
    color: "#ffffff",
    backgroundColor: type === "success" ? "#059669" : "#dc2626",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
    minWidth: "300px",
    animation: "slideInRight 0.3s ease-out",
  });

  return (
    <>
      <NewSideBarMagasins />
      {popup.visible && (
        <div style={popupStyle(popup.type)}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", fontWeight: 700, fontSize: "1.1rem", marginBottom: "8px" }}>
            <i className={`bi bi-${popup.type === "success" ? "check-circle" : "x-circle"}`}></i>
            {popup.title}
          </div>
          <p style={{ margin: 0, paddingLeft: "26px", fontSize: "0.95rem", opacity: 0.9 }}>{popup.message}</p>
        </div>
      )}
      <div className="sf-page">
        <div className="sf-inner">
          {/* Back */}
          <button className="sf-back" onClick={handleGoBack}>
            <i className="bi bi-arrow-left"></i> Retour
          </button>

          {/* Header */}
          <div className="sf-header">
            <div className="sf-header-icon">
              <i className="bi bi-shield-lock"></i>
            </div>
            <div className="sf-header-text">
              <h1>Changer le mot de passe</h1>
              <p>Sécurisez votre compte avec un nouveau mot de passe</p>
            </div>
          </div>

          {/* Card */}
          <div className="sf-card">
            {/* Old password */}
            <div className="sf-group">
              <label className="sf-label">Ancien mot de passe</label>
              <div className="sf-input-wrap">
                <i className="bi bi-lock sf-field-icon"></i>
                <input
                  className="sf-input"
                  type={showOld ? "text" : "password"}
                  placeholder="Entrez votre mot de passe actuel"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="sf-toggle-vis"
                  onClick={() => setShowOld(!showOld)}
                >
                  <i className={`bi ${showOld ? "bi-eye-slash" : "bi-eye"}`}></i>
                </button>
              </div>
            </div>

            <div className="sf-divider"></div>

            {/* New password */}
            <div className="sf-group">
              <label className="sf-label">Nouveau mot de passe</label>
              <div className="sf-input-wrap">
                <i className="bi bi-key sf-field-icon"></i>
                <input
                  className="sf-input"
                  type={showNew ? "text" : "password"}
                  placeholder="Créez un mot de passe sécurisé"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="sf-toggle-vis"
                  onClick={() => setShowNew(!showNew)}
                >
                  <i className={`bi ${showNew ? "bi-eye-slash" : "bi-eye"}`}></i>
                </button>
              </div>
              {/* Strength meter */}
              {newPassword && (
                <>
                  <div className="sf-strength">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className={`sf-strength-bar ${strengthClass(i)}`} />
                    ))}
                  </div>
                  <div className={`sf-strength-label ${strength.level === 1 ? "weak" : strength.level === 2 ? "medium" : "strong"}`}>
                    {strength.label}
                  </div>
                </>
              )}
              <div className="sf-hint">
                <i className="bi bi-info-circle"></i>
                Minimum 8 caractères, avec majuscules et chiffres
              </div>
            </div>

            {/* Confirm password */}
            <div className="sf-group">
              <label className="sf-label">Confirmer le mot de passe</label>
              <div className="sf-input-wrap">
                <i className="bi bi-check2-circle sf-field-icon"></i>
                <input
                  className="sf-input"
                  type={showConfirm ? "text" : "password"}
                  placeholder="Retapez le nouveau mot de passe"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="sf-toggle-vis"
                  onClick={() => setShowConfirm(!showConfirm)}
                >
                  <i className={`bi ${showConfirm ? "bi-eye-slash" : "bi-eye"}`}></i>
                </button>
              </div>
              {confirmPassword && newPassword && confirmPassword !== newPassword && (
                <div className="sf-hint" style={{ color: "#EF4444" }}>
                  <i className="bi bi-exclamation-circle"></i>
                  Les mots de passe ne correspondent pas
                </div>
              )}
              {confirmPassword && newPassword && confirmPassword === newPassword && (
                <div className="sf-hint" style={{ color: "#22C55E" }}>
                  <i className="bi bi-check-circle"></i>
                  Les mots de passe correspondent
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="sf-actions">
              <button className="sf-submit" onClick={handleSubmit} disabled={loadingSubmit}>
                {loadingSubmit ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  <>
                    <i className="bi bi-check-lg"></i>
                    Changer le mot de passe
                  </>
                )}
              </button>
              <button className="sf-cancel" onClick={handleGoBack}>
                Annuler
              </button>
            </div>
          </div>

          {/* Help */}
          <div className="sf-help">
            <i className="bi bi-question-circle"></i>
            Mot de passe oublié ?&nbsp;
            <a href="/contacter-nous">Contactez-nous</a>
          </div>
        </div>
      </div>
    </>
  );
}